import React, { useCallback, useEffect, useState } from "react";
import { useDesign } from "../../context/contextDesign";
import { useThemeMode } from "../../context/contextTheme";
import {
  createWordpressSiteKey,
  deleteWordpressSite,
  listWordpressSiteKeys,
  listWordpressSites,
  registerWordpressSite,
  revokeWordpressSiteKey,
  wordpressApiBase,
} from "../../api/wordpress";
import "./wordpress-sites.css";

function readableDate(value) {
  if (!value) return "Never";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

function CredentialPanel({ credential, onClose }) {
  const [copied, setCopied] = useState("");
  if (!credential) return null;

  const fields = [
    ["SAVE API URL", wordpressApiBase],
    ["Site ID", credential.siteId],
    ["Site API key", credential.apiKey],
  ];

  const copy = async (label, value) => {
    await navigator.clipboard.writeText(value);
    setCopied(label);
    window.setTimeout(() => setCopied(""), 1200);
  };

  return (
    <section className="wp-connect-package" aria-live="polite">
      <div className="wp-connect-package__heading">
        <div>
          <span className="wp-eyebrow">One-time connection package</span>
          <h3>Connect the SAVE Security Scanner plugin</h3>
        </div>
        <button type="button" className="btn-close" aria-label="Hide credentials" onClick={onClose} />
      </div>
      <p className="wp-secret-warning">
        Copy these values into WordPress now. The API key is shown once and cannot be recovered by SAVE.
      </p>
      <div className="wp-credential-list">
        {fields.map(([label, value]) => (
          <div className="wp-credential" key={label}>
            <span>{label}</span>
            <code>{value}</code>
            <button type="button" className="btn btn-sm btn-outline-light" onClick={() => copy(label, value)}>
              {copied === label ? "Copied" : "Copy"}
            </button>
          </div>
        ))}
      </div>
      <ol className="wp-connect-steps">
        <li>Install and activate <strong>SAVE Security Scanner</strong> in WordPress.</li>
        <li>Open <strong>WP Admin → SAVE Security</strong> and paste the three values above.</li>
        <li>Create the meta verification token, clear any page cache, and select <strong>Verify now</strong>.</li>
      </ol>
    </section>
  );
}

export default function WordPressSites() {
  const { bHeightVal, zoom } = useDesign();
  const { theme } = useThemeMode();
  const [sites, setSites] = useState([]);
  const [keys, setKeys] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [credential, setCredential] = useState(null);
  const [form, setForm] = useState({ displayName: "", siteUrl: "" });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null);

  const loadSites = useCallback(async () => {
    setLoading(true);
    try {
      setSites(await listWordpressSites());
    } catch (error) {
      setNotice({ type: "danger", message: error.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSites(); }, [loadSites]);

  const register = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setNotice(null);
    try {
      const created = await registerWordpressSite(form);
      setCredential({
        siteId: created.site.siteId,
        apiKey: created.credential.apiKey,
      });
      setForm({ displayName: "", siteUrl: "" });
      setNotice({ type: "success", message: "Website registered. Copy the one-time connection package below." });
      await loadSites();
    } catch (error) {
      setNotice({ type: "danger", message: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleKeys = async (siteId) => {
    if (expanded === siteId) {
      setExpanded(null);
      return;
    }
    setExpanded(siteId);
    try {
      const siteKeys = await listWordpressSiteKeys(siteId);
      setKeys((current) => ({ ...current, [siteId]: siteKeys }));
    } catch (error) {
      setNotice({ type: "danger", message: error.message });
    }
  };

  const rotate = async (site) => {
    try {
      const next = await createWordpressSiteKey(site.siteId);
      setCredential({ siteId: site.siteId, apiKey: next.apiKey });
      const siteKeys = await listWordpressSiteKeys(site.siteId);
      setKeys((current) => ({ ...current, [site.siteId]: siteKeys }));
      setNotice({ type: "warning", message: "New key created. Update WordPress before revoking the old key." });
    } catch (error) {
      setNotice({ type: "danger", message: error.message });
    }
  };

  const revoke = async (siteId, keyId) => {
    if (!window.confirm("Revoke this key? A WordPress site using it will disconnect immediately.")) return;
    try {
      await revokeWordpressSiteKey(siteId, keyId);
      const siteKeys = await listWordpressSiteKeys(siteId);
      setKeys((current) => ({ ...current, [siteId]: siteKeys }));
    } catch (error) {
      setNotice({ type: "danger", message: error.message });
    }
  };

  const remove = async (site) => {
    if (!window.confirm(`Remove ${site.displayName} from SAVE? Scan history for this integration will no longer be available.`)) return;
    try {
      await deleteWordpressSite(site.siteId);
      if (credential?.siteId === site.siteId) setCredential(null);
      await loadSites();
    } catch (error) {
      setNotice({ type: "danger", message: error.message });
    }
  };

  return (
    <div className="wordpress-sites-page overflow-y-auto" style={{ height: bHeightVal, zoom, color: theme.textColor }}>
      <div className="wordpress-sites-shell">
        <header className="wordpress-sites-hero">
          <div>
            <span className="wp-eyebrow">SAVE distribution</span>
            <h2>WordPress Sites</h2>
            <p>Connect a lightweight WordPress plugin to SAVE Cloud. Scanners and AI processing remain on SAVE infrastructure.</p>
          </div>
          <div className="wp-cloud-pill"><i className="bi bi-cloud-check" /> Cloud scanning</div>
        </header>

        {notice && <div className={`alert alert-${notice.type}`} role="status">{notice.message}</div>}

        <section className="wp-register-card">
          <div>
            <h3>Register a website</h3>
            <p>Only public HTTPS websites are accepted. SAVE verifies ownership before any scan can start.</p>
          </div>
          <form onSubmit={register} className="wp-register-form">
            <label>
              Display name
              <input className="form-control" value={form.displayName} maxLength="191" required placeholder="Company website" onChange={(event) => setForm({ ...form, displayName: event.target.value })} />
            </label>
            <label>
              WordPress URL
              <input className="form-control" type="url" value={form.siteUrl} required placeholder="https://example.com" onChange={(event) => setForm({ ...form, siteUrl: event.target.value })} />
            </label>
            <button className="btn btn-primary" disabled={submitting}>{submitting ? "Registering…" : "Register website"}</button>
          </form>
        </section>

        <CredentialPanel credential={credential} onClose={() => setCredential(null)} />

        <section className="wp-sites-list">
          <div className="wp-section-heading">
            <div><h3>Registered sites</h3><p>{sites.length} website{sites.length === 1 ? "" : "s"} connected to this account</p></div>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadSites}>Refresh</button>
          </div>
          {loading ? <div className="wp-empty">Loading registered websites…</div> : sites.length === 0 ? (
            <div className="wp-empty"><i className="bi bi-wordpress" /><strong>No WordPress sites yet</strong><span>Register the first site above to generate its one-time connection package.</span></div>
          ) : sites.map((site) => (
            <article className="wp-site-card" key={site.siteId}>
              <div className="wp-site-main">
                <div className="wp-site-icon"><i className="bi bi-wordpress" /></div>
                <div className="wp-site-identity">
                  <h4>{site.displayName}</h4>
                  <a href={site.siteUrl} target="_blank" rel="noreferrer">{site.siteUrl}</a>
                  <div className="wp-site-meta">Last connected: {readableDate(site.lastSeenAt)}</div>
                </div>
                <span className={`wp-status wp-status--${site.status}`}>{String(site.status).replaceAll("_", " ")}</span>
              </div>
              <div className="wp-site-actions">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => toggleKeys(site.siteId)}>{expanded === site.siteId ? "Hide keys" : "Manage keys"}</button>
                <button type="button" className="btn btn-sm btn-primary" onClick={() => rotate(site)}>Generate new key</button>
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => remove(site)}>Remove</button>
              </div>
              {expanded === site.siteId && (
                <div className="wp-key-list">
                  {(keys[site.siteId] || []).map((key) => (
                    <div className="wp-key-row" key={key.keyId}>
                      <div><code>{key.keyHint}</code><span>Created {readableDate(key.createdAt)} · Last used {readableDate(key.lastUsedAt)}</span></div>
                      <span className={key.revokedAt ? "wp-key-revoked" : "wp-key-active"}>{key.revokedAt ? "Revoked" : "Active"}</span>
                      {!key.revokedAt && <button type="button" className="btn btn-sm btn-link text-danger" onClick={() => revoke(site.siteId, key.keyId)}>Revoke</button>}
                    </div>
                  ))}
                </div>
              )}
            </article>
          ))}
        </section>
      </div>
    </div>
  );
}
